import { EXTRA_ACTIVITY_TYPES } from 'common/ActivityChip/utils/extraActivities';
import { generateISOfromStrings, resetTimestampToUtc } from 'utils/datetime';

export const COL_WIDTH = 120;
export const COLUMNS_IN_BASE_WIDTH = 2;

export const pad2 = (n) => String(n).padStart(2, '0');

export const formatDateTime = (iso) => {
	if (!iso) return 'N/A';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return 'N/A';

	return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(
		d.getHours()
	)}:${pad2(d.getMinutes())}`;
};

export const titleCase = (s) =>
	typeof s === 'string' && s.length ? s[0].toUpperCase() + s.slice(1) : 'N/A';

export const getPlannedDeparture = (sequences) => sequences?.[0]?.fromTimestamp ?? null;

export const getPlannedArrival = (sequences) =>
	sequences?.[sequences.length - 1]?.toTimestamp ?? null;

export const getRidePlannedDeparture = (sequences) => {
	const firstSeq = sequences?.[0];
	if (!firstSeq) return null;

	const fromStationId = firstSeq.fromStation?.id;
	const rideStations = firstSeq.ride?.stations;

	if (!fromStationId || !Array.isArray(rideStations)) return null;

	const station = rideStations.find((s) => s?.id === fromStationId);
	return station?.departure ?? null;
};

export const getRidePlannedArrival = (sequences) => {
	const lastSeq = sequences?.[sequences.length - 1];
	if (!lastSeq) return null;

	const toStationId = lastSeq.toStation?.id;
	const rideStations = lastSeq.ride?.stations;

	if (!toStationId || !Array.isArray(rideStations)) return null;

	const station = rideStations.find((s) => s?.id === toStationId);
	return station?.arrival ?? null;
};

const getTimeMs = (value) => {
	if (!value) return null;
	const ms = new Date(value).getTime();
	return Number.isFinite(ms) ? ms : null;
};

export const getExtraActivities = (sequences) => {
	if (!Array.isArray(sequences)) return [];

	return sequences.flatMap((seq) => {
		if (!Array.isArray(seq?.extraActivities)) return [];

		const tripNumber = seq.ride.tripNumber;

		return seq.extraActivities.map((activity) => ({ ...activity, tripNumber }));
	});
};

const PRE_DRIVING_TYPES = [EXTRA_ACTIVITY_TYPES.signOn, EXTRA_ACTIVITY_TYPES.garaging];

const POST_DRIVING_TYPES = [
	EXTRA_ACTIVITY_TYPES.signOff,
	EXTRA_ACTIVITY_TYPES.garaging,
	EXTRA_ACTIVITY_TYPES.fuelling,
	EXTRA_ACTIVITY_TYPES.cleaning,
];

const isAtStation = (activity, stationCode) =>
	activity?.fromStation?.code === stationCode || activity?.toStation?.code === stationCode;

export const getPlannedAssignmentStart = (sequences) => {
	if (!Array.isArray(sequences) || sequences.length === 0) return null;

	const rideDeparture = getRidePlannedDeparture(sequences);
	if (!rideDeparture) return null;

	const firstSeq = sequences[0];
	const departureStationCode = firstSeq?.fromStation?.code;

	const extras = getExtraActivities([firstSeq]);
	const preDrivingAtDeparture = extras.filter(
		(a) => PRE_DRIVING_TYPES.includes(a?.activityType) && isAtStation(a, departureStationCode)
	);

	const totalDurationMs = preDrivingAtDeparture.reduce((sum, a) => {
		const fromMs = getTimeMs(a?.fromTimestamp);
		const toMs = getTimeMs(a?.toTimestamp);
		if (fromMs === null || toMs === null) return sum;
		return sum + Math.max(0, toMs - fromMs);
	}, 0);

	if (totalDurationMs === 0) return rideDeparture;

	const departureMs = getTimeMs(rideDeparture);
	if (departureMs === null) return rideDeparture;

	const adjustedMs = departureMs - totalDurationMs;
	if (adjustedMs < 0) return rideDeparture;

	return resetTimestampToUtc(adjustedMs);
};

export const getPlannedAssignmentEnd = (sequences) => {
	if (!Array.isArray(sequences) || sequences.length === 0) return null;

	const rideArrival = getRidePlannedArrival(sequences);
	if (!rideArrival) return null;

	const lastSeq = sequences[sequences.length - 1];
	const arrivalStationCode = lastSeq?.toStation?.code;

	const extras = getExtraActivities([lastSeq]);
	const postDrivingAtArrival = extras.filter(
		(a) => POST_DRIVING_TYPES.includes(a?.activityType) && isAtStation(a, arrivalStationCode)
	);

	const totalDurationMs = postDrivingAtArrival.reduce((sum, a) => {
		const fromMs = getTimeMs(a?.fromTimestamp);
		const toMs = getTimeMs(a?.toTimestamp);
		if (fromMs === null || toMs === null) return sum;
		return sum + Math.max(0, toMs - fromMs);
	}, 0);

	if (totalDurationMs === 0) return rideArrival;

	const arrivalMs = getTimeMs(rideArrival);
	if (arrivalMs === null) return rideArrival;

	return resetTimestampToUtc(arrivalMs + totalDurationMs);
};

const EXTRA_ACTIVITY_LABELS = {
	sign_on: 'Sign On',
	sign_off: 'Sign Off',
	signon: 'Sign On',
	signoff: 'Sign Off',
	signOn: 'Sign On',
	signOff: 'Sign Off',
	lapse_time: 'Lapse Time',
	lapsetime: 'Lapse Time',
	lapseTime: 'Lapse Time',
	garaging: 'Garaging',
	break: 'Break',
	briefing: 'Briefing',
	handover: 'Handover',
	deadhead: 'Deadhead',
};

const formatExtraActivityLabel = (activityType) => {
	if (!activityType) return 'Activity';

	const key = String(activityType).toLowerCase();
	if (EXTRA_ACTIVITY_LABELS[key]) return EXTRA_ACTIVITY_LABELS[key];

	return key
		.split(/[_\s]+/)
		.filter(Boolean)
		.map((part) => part[0].toUpperCase() + part.slice(1))
		.join(' ');
};

export const buildExtraActivityItems = (sequences) => {
	const activities = getExtraActivities(sequences);

	return activities
		.map((activity, idx) => {
			const fromMs = getTimeMs(activity?.fromTimestamp);
			const toMs = getTimeMs(activity?.toTimestamp);
			const label = formatExtraActivityLabel(activity?.activityType);
			const stationCode = activity?.fromStation?.code ?? activity?.toStation?.code ?? null;
			const durationMinutes =
				fromMs !== null && toMs !== null ? Math.max(0, Math.round((toMs - fromMs) / 60000)) : null;

			return {
				key: idx.toString(),
				label,
				durationMinutes,
				stationCode,
				fromMs,
				tripNumber: activity.tripNumber,
			};
		})
		.sort((a, b) => {
			if (a.fromMs === null && b.fromMs === null) return 0;
			if (a.fromMs === null) return 1;
			if (b.fromMs === null) return -1;
			return a.fromMs - b.fromMs;
		});
};

export const getRouteText = (sequences) => {
	const first = sequences?.[0];
	const last = sequences?.[sequences.length - 1];
	const from = first?.fromStation?.name ?? 'N/A';
	const to = last?.toStation?.name ?? 'N/A';

	return `${from} → ${to}`;
};

// Insert a synthetic (virtual) station next to its physical anchor.
// - `placement: 'after'` → drops in right after the anchor (used when the
//   virtual is a `toStation`, so `slice(fromIdx, toIdx + 1)` yields
//   exactly [anchor, virtual]).
// - `placement: 'before'` → drops in right before the anchor (used when
//   the virtual is a `fromStation`, so the slice yields [virtual, anchor, …]).
// If the anchor is missing, falls back to the array's edge.
const spliceVirtualNearAnchor = ({ stations, virtualRef, anchorId, placement }) => {
	const anchorIdx = anchorId ? stations.findIndex((station) => station?.id === anchorId) : -1;
	const fallback = placement === 'after' ? stations.length : 0;
	const insertAt = anchorIdx >= 0 ? anchorIdx + (placement === 'after' ? 1 : 0) : fallback;
	stations.splice(insertAt, 0, { ...virtualRef, isVirtual: true });
};

// Breakdown locations may appear as sequence endpoints without being
// present in the sequence's `ride.stations`. Splice a synthetic entry
// adjacent to the physical anchor it neighbours, so the resulting chain
// terminates at the breakdown station and does NOT drag every station
// after the anchor into the slice. Downstream renderers use `isVirtual`
// to swap the label for a maintenance badge.
const withVirtualEndpoints = (rideStations, sequence) => {
	const rideStationIds = new Set(rideStations.map((station) => station?.id).filter(Boolean));
	const fromRef = sequence?.fromStation ?? null;
	const toRef = sequence?.toStation ?? null;
	const fromIsMissing = fromRef?.id && !rideStationIds.has(fromRef.id);
	const toIsMissing = toRef?.id && !rideStationIds.has(toRef.id);

	if (!fromIsMissing && !toIsMissing) return rideStations;

	const enriched = [...rideStations];

	if (toIsMissing) {
		const workAssignments = sequence?.workAssignments ?? [];
		const lastWaFromId = workAssignments[workAssignments.length - 1]?.fromStation?.id;
		spliceVirtualNearAnchor({
			stations: enriched,
			virtualRef: toRef,
			anchorId: lastWaFromId ?? enriched[enriched.length - 1]?.id,
			placement: 'after',
		});
	}

	if (fromIsMissing) {
		const firstWaToId = sequence?.workAssignments?.[0]?.toStation?.id;
		spliceVirtualNearAnchor({
			stations: enriched,
			virtualRef: fromRef,
			// To ensure the station chain includes all intermediate stations
			anchorId: firstWaToId ?? toRef?.id,
			placement: 'before',
		});
	}

	return enriched;
};

export const buildStationChain = (sequences) => {
	if (!Array.isArray(sequences) || sequences.length === 0) return [];

	const chain = [];

	for (const seq of sequences) {
		const rawRideStations = seq?.ride?.stations;
		const fromId = seq?.fromStation?.id;
		const toId = seq?.toStation?.id;

		if (!Array.isArray(rawRideStations) || rawRideStations.length === 0) continue;
		if (!fromId || !toId) continue;

		const rideStations = withVirtualEndpoints(rawRideStations, seq);

		const fromIdx = rideStations.findIndex((s) => s?.id === fromId);
		const toIdx = rideStations.findIndex((s) => s?.id === toId);

		if (fromIdx < 0 || toIdx < 0) continue;
		if (toIdx < fromIdx) continue;

		const slice = rideStations.slice(fromIdx, toIdx + 1);

		for (const st of slice) {
			if (!st) continue;

			const prev = chain[chain.length - 1];

			if (!prev) {
				chain.push(st);
				continue;
			}

			if (prev.id === st.id) {
				chain[chain.length - 1] = {
					...prev,
					...st,
					arrival: prev.arrival,
					departure: st.departure,
				};
				continue;
			}

			chain.push(st);
		}
	}

	return chain;
};
export const totalColsFromStations = (stationsCount) => {
	if (stationsCount <= 1) return 1;
	return stationsCount * 2 - 2;
};

export const calcRegularColumnPlacement = (indexFrom, indexTo) => {
	return {
		from: indexFrom * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2,
		to: indexTo * COLUMNS_IN_BASE_WIDTH + COLUMNS_IN_BASE_WIDTH / 2,
	};
};

export const buildRideItems = (sequences, stations) => {
	if (!Array.isArray(sequences) || sequences.length === 0) return [];
	if (!Array.isArray(stations) || stations.length === 0) return [];

	const idxMap = new Map();

	stations.forEach((st, idx) => {
		if (!st?.id) return;

		if (!idxMap.has(st.id)) {
			idxMap.set(st.id, []);
		}

		idxMap.get(st.id).push(idx);
	});

	let lastToIdx = -1;

	const takeIdx = (stationId, minIdx = -1, allowEqual = false) => {
		const indexes = idxMap.get(stationId);

		if (!indexes?.length) return undefined;

		return indexes.find((idx) => (allowEqual ? idx >= minIdx : idx > minIdx));
	};

	return sequences.map((seq, seqIdx) => {
		const fromId = seq?.fromStation?.id;
		const toId = seq?.toStation?.id;
		const fromIdx = fromId ? takeIdx(fromId, lastToIdx, true) : undefined;
		const toIdx = toId ? takeIdx(toId, fromIdx ?? lastToIdx, false) : undefined;
		const safeFromIdx = Number.isFinite(fromIdx) ? fromIdx : 0;
		const safeToIdx = Number.isFinite(toIdx) && toIdx > safeFromIdx ? toIdx : safeFromIdx + 1;
		const { from, to } = calcRegularColumnPlacement(safeFromIdx, safeToIdx);
		lastToIdx = safeToIdx;

		return {
			key: seq?.ride?.id ?? `seq-${seqIdx}`,
			title: `${seq?.fromStation?.name ?? 'N/A'} → ${seq?.toStation?.name ?? 'N/A'}`,
			subtitle: `Line: ${seq?.ride?.lineCode ?? 'N/A'}, Trip: ${
				seq?.tripNumber ?? seq?.ride?.tripNumber ?? 'N/A'
			}`,
			gridColumnStart: from,
			gridColumnEnd: to <= from ? from + 1 : to,
			id: seq?.ride?.id,
		};
	});
};

export const tagAppearance = (status) => {
	switch (status) {
		case 'failed':
			return 'danger';
		case 'pending':
			return undefined;
		default:
			return 'success';
	}
};

export const deriveAssignment = ({ sequences, stations, assignment }) => {
	const firstStation = stations[0] ?? null;
	const lastStation = stations[stations.length - 1] ?? null;
	const firstSequence = sequences[0] ?? null;
	const lastSequence = sequences[sequences.length - 1] ?? null;
	const firstSequenceWorkAssignment = firstSequence?.workAssignments?.[0] ?? null;
	const lastSequenceWorkAssignment =
		lastSequence?.workAssignments?.[lastSequence?.workAssignments?.length - 1] ?? null;

	return {
		firstStation,
		lastStation,
		departureTimezone: firstStation?.timezone ?? firstSequenceWorkAssignment?.fromStation?.timezone,
		arrivalTimezone: lastStation?.timezone ?? lastSequenceWorkAssignment?.toStation?.timezone,
		firstSequence,
		lastSequence,
		sequenceGroupId: assignment?.sequenceGroupId ?? firstSequence?.sequenceGroupId ?? '',
		firstSequenceWorkAssignment,
		lastSequenceWorkAssignment,
	};
};

export const buildReconciliationTimeRangeInputs = (formData, meta) => {
	const { departureDate, departureTime, arrivalDate, arrivalTime } = formData;
	const {
		firstSequenceWorkAssignment,
		lastSequenceWorkAssignment,
		firstSequence,
		lastSequence,
		departureTimezone,
		arrivalTimezone,
	} = meta;
	const fromTimestamp = generateISOfromStrings(departureDate, departureTime, departureTimezone);
	const toTimestamp = generateISOfromStrings(arrivalDate, arrivalTime, arrivalTimezone);

	if (firstSequenceWorkAssignment.id === lastSequenceWorkAssignment.id) {
		return [
			{
				key: { id: firstSequenceWorkAssignment.id, version: firstSequenceWorkAssignment.version },
				value: { fromTimestamp, toTimestamp },
			},
		];
	}

	return [
		{
			key: { id: firstSequenceWorkAssignment.id, version: firstSequenceWorkAssignment.version },
			value: {
				fromTimestamp,
				toTimestamp: firstSequenceWorkAssignment.toTimestamp ?? firstSequence.toTimestamp,
			},
		},
		{
			key: { id: lastSequenceWorkAssignment.id, version: lastSequenceWorkAssignment.version },
			value: {
				fromTimestamp: lastSequenceWorkAssignment.fromTimestamp ?? lastSequence.fromTimestamp,
				toTimestamp,
			},
		},
	];
};

export const buildStationActuals = (sequences, stations) => {
	if (!Array.isArray(sequences) || sequences.length === 0) return [];
	if (!Array.isArray(stations) || stations.length === 0) return [];

	const result = stations.map(() => ({ actualArrival: null, actualDeparture: null }));

	let chainCursor = 0;

	for (const seq of sequences) {
		const rideStations = seq?.ride?.stations;
		const fromId = seq?.fromStation?.id;
		const toId = seq?.toStation?.id;

		if (!Array.isArray(rideStations) || rideStations.length === 0) continue;
		if (!fromId || !toId) continue;

		const fromIdx = rideStations.findIndex((s) => s?.id === fromId);
		const toIdx = rideStations.findIndex((s) => s?.id === toId);
		if (fromIdx < 0 || toIdx < 0) continue;
		if (toIdx < fromIdx) continue;

		const slice = rideStations.slice(fromIdx, toIdx + 1);

		const sliceStationIdToChainIdx = new Map();
		for (const st of slice) {
			if (!st?.id) continue;
			const prevChainStation = chainCursor > 0 ? stations[chainCursor - 1] : null;
			const isBoundaryMerge = prevChainStation?.id === st.id;
			const chainIdx = isBoundaryMerge ? chainCursor - 1 : chainCursor;
			if (!sliceStationIdToChainIdx.has(st.id)) {
				sliceStationIdToChainIdx.set(st.id, chainIdx);
			}
			if (!isBoundaryMerge) chainCursor += 1;
		}

		if (seq.status !== 'actual') continue;

		const workAssignments = Array.isArray(seq.workAssignments) ? seq.workAssignments : [];
		for (const wa of workAssignments) {
			const waFromIdx = sliceStationIdToChainIdx.get(wa?.fromStation?.id);
			const waToIdx = sliceStationIdToChainIdx.get(wa?.toStation?.id);

			if (waFromIdx !== undefined && result[waFromIdx].actualDeparture === null) {
				result[waFromIdx].actualDeparture = wa?.fromTimestamp ?? null;
			}
			if (waToIdx !== undefined && result[waToIdx].actualArrival === null) {
				result[waToIdx].actualArrival = wa?.toTimestamp ?? null;
			}
		}
	}

	return result;
};

export const buildAssignmentLabel = (stations) => {
	if (stations.length === 0) return 'N/A';
	const first = stations[0];
	const last = stations[stations.length - 1];
	return `${first.code} (${first.name}) → ${last.code} (${last.name})`;
};

export const deriveSequenceData = ({ sequences, assignment }) => {
	const plannedDeparture = getPlannedDeparture(sequences);
	const plannedArrival = getPlannedArrival(sequences);
	const ridePlannedDeparture = getRidePlannedDeparture(sequences);
	const ridePlannedArrival = getRidePlannedArrival(sequences);
	const plannedAssignmentStart = getPlannedAssignmentStart(sequences);
	const plannedAssignmentEnd = getPlannedAssignmentEnd(sequences);
	const stations = buildStationChain(sequences);
	const stationActuals = buildStationActuals(sequences, stations);
	const rideItems = buildRideItems(sequences, stations);
	const extraActivityItems = buildExtraActivityItems(sequences);

	return {
		plannedDeparture,
		plannedArrival,
		ridePlannedDeparture,
		ridePlannedArrival,
		plannedAssignmentStart,
		plannedAssignmentEnd,
		stations,
		stationActuals,
		rideItems,
		extraActivityItems,
		...deriveAssignment({ sequences, stations, assignment }),
	};
};

export const toCrewMemberState = (crewMember) =>
	crewMember ? { id: crewMember.id, fullName: crewMember.fullName } : null;
